import { useToast } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FoodType } from '@/modules/food/domain/models/FoodType'
import { foodStockInstance } from '@/modules/foodStock'
import { FoodStock } from '@/modules/foodStock/domain/models/FoodStock'
import { CreateFoodStockFoodItemDTO } from '@/modules/foodStock/domain/models/FoodStockFoodItem'
import { usePatientContext } from '@/ui/contexts/PatientContext'
import { getListPatientsFoodStockQuery } from '@/ui/hooks/queries/useListPatientFoodStockQuery'

export interface SelectedFoodItem {
  name: string
  quantity: number
  id: string
  type: FoodType
}

type AddBatchItem = CreateFoodStockFoodItemDTO & {
  stockFoodId: string
  index?: number
}

type FoodStockType = {
  name: string
  minimumQuantity: number
  id: string
  index: number
}

type FoodStockPage = 'ADD_STOCK' | 'STOCK_LIST'

const foodStockContext = createContext<{
  addStock: boolean
  setAddStock: React.Dispatch<React.SetStateAction<boolean>>
  addBatch: AddBatchItem[]
  setAddBatch: React.Dispatch<React.SetStateAction<AddBatchItem[]>>
  addBatchItem: AddBatchItem | null
  setAddBatchItem: React.Dispatch<React.SetStateAction<AddBatchItem | null>>
  foodList: SelectedFoodItem[]
  setFoodList: React.Dispatch<React.SetStateAction<SelectedFoodItem[]>>
  setIsOpenNewFoodModal: React.Dispatch<React.SetStateAction<boolean>>
  isOpenNewFoodModal: boolean
  isNewBatch: boolean
  setIsNewBatch: React.Dispatch<React.SetStateAction<boolean>>
  selectedFood: FoodStock | null
  setSelectedFood: React.Dispatch<React.SetStateAction<FoodStock | null>>
  isEditBatch: boolean
  setIsEditBatch: React.Dispatch<React.SetStateAction<boolean>>
  addStockItem: (data?: {
    onSuccess?: () => void
    foodItems?: AddBatchItem[]
  }) => Promise<void>
  openFoodList: boolean
  setOpenFoodList: React.Dispatch<React.SetStateAction<boolean>>
  foodBatch: FoodStockType
  setFoodBatch: React.Dispatch<React.SetStateAction<FoodStockType>>
  stockPage: FoodStockPage
  setStockPage: React.Dispatch<React.SetStateAction<FoodStockPage>>
  isOnlyAddBatchMode: boolean
  setIsOnlyAddBatchMode: React.Dispatch<React.SetStateAction<boolean>>
}>({
  addBatch: [],
  setAddBatch: () => {},
  setIsOpenNewFoodModal: () => {},
  isOpenNewFoodModal: false,
  foodList: [],
  setFoodList: () => {},
  isNewBatch: false,
  setIsNewBatch: () => {},
  selectedFood: null,
  setSelectedFood: () => {},
  isEditBatch: false,
  setIsEditBatch: () => {},
  addBatchItem: null,
  setAddBatchItem: () => {},
  addStockItem: async () => {},
  openFoodList: false,
  setOpenFoodList: () => {},
  foodBatch: {
    name: '',
    minimumQuantity: 0,
    id: '',
    index: 0
  },
  setFoodBatch: () => {},
  addStock: false,
  setAddStock: () => {},
  stockPage: 'STOCK_LIST',
  setStockPage: () => {},
  isOnlyAddBatchMode: false,
  setIsOnlyAddBatchMode: () => {}
})

export const useFoodStockContext = () => useContext(foodStockContext)

export default function FoodStockProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { patient } = usePatientContext()
  if (!patient) return

  const [addStock, setAddStock] = useState<boolean>(false)
  const [isOpenNewFoodModal, setIsOpenNewFoodModal] = useState(false)
  const [foodList, setFoodList] = useState<SelectedFoodItem[]>([])
  const [addBatch, setAddBatch] = useState<AddBatchItem[]>([])
  const [isNewBatch, setIsNewBatch] = useState(false)
  const [selectedFood, setSelectedFood] = useState<FoodStock | null>(null)
  const [isEditBatch, setIsEditBatch] = useState(false)
  const [addBatchItem, setAddBatchItem] = useState<AddBatchItem | null>(null)
  const [openFoodList, setOpenFoodList] = useState(false)
  const [foodBatch, setFoodBatch] = useState<FoodStockType>({
    name: '',
    minimumQuantity: 0,
    id: '',
    index: 0
  })
  const [stockPage, setStockPage] = useState<FoodStockPage>('STOCK_LIST')
  const [isOnlyAddBatchMode, setIsOnlyAddBatchMode] = useState(false)

  const addStockItem = useCallback(
    async function ({
      onSuccess,
      foodItems
    }: { onSuccess?: () => void; foodItems?: AddBatchItem[] } = {}) {
      try {
        if (addStock) {
          await Promise.all(
            (foodItems ?? addBatch).map(({ index: _index, ...batch }) =>
              foodStockInstance.createItemFoodStock(batch)
            )
          )
          if ((foodItems ?? addBatch).length)
            toast({
              status: 'success',
              description: t('general.form.successfulAdd')
            })
        } else {
          await Promise.all(
            foodList.map((food) => {
              return foodStockInstance.createFoodStockFoodCase([
                {
                  foodId: food.id,
                  minimumQuantity: food.quantity,
                  patientId: patient?.id ?? '',
                  placeId: selectedFood?.id ?? '',
                  items: addBatch
                    .filter(({ stockFoodId }) => stockFoodId === food.id)
                    .map((item) => ({
                      condition: item.condition,
                      expirationDate: item.expirationDate,
                      quantity: item.quantity,
                      validityAfterOpening: item.validityAfterOpening
                    }))
                }
              ])
            })
          )
          foodList.length &&
            toast({
              status: 'success',
              description: t('general.form.successfulAdd')
            })
        }

        queryClient.invalidateQueries({
          queryKey: getListPatientsFoodStockQuery()
        })
        onSuccess?.()
      } catch (error) {
        console.error(error)
        toast({
          status: 'error',
          description: t('general.form.failedGeneric')
        })
      } finally {
        setAddBatch([])
        setFoodList([])
        setStockPage('STOCK_LIST')
        setAddStock(false)
      }
    },
    [addBatch, foodList, selectedFood, addStock]
  )

  return (
    <foodStockContext.Provider
      value={{
        addStock,
        setAddStock,
        addStockItem,
        addBatchItem,
        setAddBatchItem,
        isEditBatch,
        setIsEditBatch,
        selectedFood,
        setSelectedFood,
        isNewBatch,
        setIsNewBatch,
        addBatch,
        setAddBatch,
        isOpenNewFoodModal,
        setIsOpenNewFoodModal,

        foodList,
        setFoodList,
        openFoodList,
        setOpenFoodList,
        foodBatch,
        setFoodBatch,

        stockPage,
        setStockPage,
        isOnlyAddBatchMode,
        setIsOnlyAddBatchMode
      }}
    >
      {children}
    </foodStockContext.Provider>
  )
}
